class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartDrawerInner = this.querySelector(".m-cart-drawer__inner");
    this.cartDrawerCloseIcon = this.querySelector(".m-cart-drawer__close");
    this.cartOverlay = this.querySelector(".m-cart__overlay");
    this.rootUrl = window.Shopify.routes.root;

    this.setHeaderCartIconAccessibility();
    this.cartDrawerCloseIcon.addEventListener("click", this.close.bind(this));
    this.addEventListener("click", (event) => {
      if (event.target.closest(".m-cart-drawer__inner") !== this.cartDrawerInner) {
        this.close();
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".m-cart-icon-bubble");
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.addEventListener("click", (event) => {
        if (MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add("m-cart-drawer--active");
    requestAnimationFrame(() => {
      this.style.setProperty("--m-bg-opacity", "0.5");
      this.cartDrawerInner.style.setProperty("--translate-x", "0");
    });
    window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
    document.documentElement.classList.add("prevent-scroll");
  }

  close() {
    this.style.setProperty("--m-bg-opacity", "0");
    this.cartDrawerInner.style.setProperty("--translate-x", "100%");
    setTimeout(() => {
      this.classList.remove("m-cart-drawer--active");
      document.documentElement.classList.remove("prevent-scroll");
    }, 300);
  }

  renderContents(parsedState) {
    this.classList.contains("m-cart--empty") && this.classList.remove("m-cart--empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) { // Added null check
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });
    setTimeout(() => {
      this.open();
    });
  }

  updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll(".m-cart-count-bubble");
    cartCounts.forEach((cartCount) => {
      if (itemCount > 0) {
        cartCount.textContent = itemCount;
        cartCount.classList.remove("m:hidden");
      } else {
        cartCount.classList.add("m:hidden");
      }
    });
  }

  getCart() {
    return fetchJSON(this.rootUrl + "cart.json");
  }

  onCartDrawerUpdate(updateFooter = true) {
    fetch(`${MinimogSettings.routes.cart}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        // Parse the responseText once to get the full HTML of the cart-drawer section
        const html = new DOMParser().parseFromString(responseText, "text/html");

        this.getSectionsToRender().forEach((section) => {
          const targetElement = section.selector
            ? this.querySelector(section.selector) // Use 'this' (the m-cart-drawer instance) to query inside
            : this.querySelector(`#${section.id}`); // If selector is just an ID, query inside 'this'

          if (targetElement) { // Ensure the element exists on the page before trying to update it
            // Find the corresponding content in the fetched HTML
            const fetchedContent = html.querySelector(section.selector || `#${section.id}`);
            if (fetchedContent) { // Ensure content was found in the fetched HTML
              targetElement.innerHTML = fetchedContent.innerHTML;
            }
          }
        });
      })
      .catch((e) => {
        console.error(e);
      });
    this.getCart().then((cart) => {
      this.classList.toggle("m-cart--empty", cart.item_count === 0);
      this.updateCartCount(cart.item_count);
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    // This method is called from renderContents, which receives parsedState.sections[section.id]
    // The parsedState.sections[section.id] itself is already the inner HTML of the section.
    // So, we don't need to parse it again with DOMParser().
    // If a selector is provided, it means it's trying to get a sub-part.
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html; // Treat html as the section's inner content
    const element = tempDiv.querySelector(selector);
    return element ? element.innerHTML : '';
  }


  getSectionsToRender() {
    return [
      {
        id: "cart-drawer", // This id refers to the section_id when fetching, not necessarily an element ID on the page
        selector: "[data-minimog-cart-items]",
        block: "cart-items",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-cart-subtotal]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
        block: "cart-footer",
      },
      // ADD THIS NEW ENTRY:
      {
        id: "cart-drawer", // The section ID that contains the footer
        selector: "#MinimogCartDrawerFooter", // The specific element to update
        block: "cart-footer", // Categorize it under footer blocks
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("m-cart-drawer", MCartDrawer);

class MCartDrawerItems extends MCartTemplate {
  // This class extends MCartTemplate, which means it inherits updateQuantity and other methods.
  // The crucial part is that when updateQuantity (from MCartTemplate) is called,
  // it uses *this* class's getSectionsToRender method.
  getSectionsToRender() {
    return [
      {
        id: "MinimogCartDrawer", // This ID is specific to the m-cart-drawer element itself
        section: "cart-drawer", // The Shopify section ID to fetch
        selector: "[data-minimog-cart-items]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-cart-subtotal]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
      },
      // ADD THIS NEW ENTRY TO MCartDrawerItems getSectionsToRender
      {
        id: "MinimogCartDrawer", // The ID of the parent element (m-cart-drawer) on the page
        section: "cart-drawer", // The Shopify section ID
        selector: "#MinimogCartDrawerFooter", // The specific element within the fetched section to update
      },
    ];
  }
}

customElements.define("m-cart-drawer-items", MCartDrawerItems);